/**
 * When something happened, in words a glance can use.
 *
 * This was private to the AI Studio's copy module, which is 1,500 lines the
 * games shelf has no other reason to load. It is here so the shelf can import
 * a relative date without pulling the studio's whole vocabulary into its
 * chunk, and `studio/copy.ts` re-exports it so every existing caller is
 * unchanged.
 */

/** Falls back to a plain date once "ago" stops being more useful than the date itself. */
export function whenWords(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((now.getTime() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "a minute ago" : `${String(minutes)} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "an hour ago" : `${String(hours)} hours ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${String(days)} days ago`;
  return calendarDay(iso);
}

/** A calendar day and short month, with no year — "12 Sep". */
export function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * The one way the billing screens say a date: "1 Nov", day first, in UTC.
 *
 * UTC because the server's periods and renewals are, and a date that moves a
 * day with the reader's timezone is two answers to "when does it renew". The
 * month is spelled here rather than by `Intl`, whose short September is "Sep"
 * in one engine and "Sept" in the next. `year` adds "2026"; `"auto"` adds it
 * only when it is not this year, which is how a next charge reads without
 * repeating the year every date shares.
 */
export function calendarDay(
  iso: string | Date,
  year: boolean | "auto" = false,
  now: Date = new Date(),
): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "";
  const shown = year === "auto" ? date.getUTCFullYear() !== now.getUTCFullYear() : year;
  const day = `${String(date.getUTCDate())} ${MONTHS[date.getUTCMonth()] ?? ""}`;
  return shown ? `${day} ${String(date.getUTCFullYear())}` : day;
}

/** "October", or "October 2026": a month's name, in UTC like the days. */
export function monthName(iso: string | Date, year = false): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  return date.toLocaleDateString("en-IN", {
    month: "long",
    timeZone: "UTC",
    ...(year ? { year: "numeric" } : {}),
  });
}
